import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthProvider from './contexts/AuthProvider';
import LocaleProvider from './contexts/LocaleProvider';
import ReviewQueueProvider from './contexts/ReviewQueueContext';
import ImpersonationProvider from './contexts/ImpersonationContext';
import FavoritesProvider from './contexts/FavoritesContext';
import AppCatalogueProvider from './contexts/AppCatalogueContext';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import SSOCallback from './pages/SSOCallback';
import ExpensesList from './pages/expenses/ExpensesList';
import ExpenseEditor from './pages/expenses/ExpenseEditor';
import ReviewQueue from './pages/expenses/ReviewQueue';
import ReviewDetail from './pages/expenses/ReviewDetail';
import Directory from './pages/Directory';
import Resources from './pages/Resources';
import Newsletters from './pages/Newsletters';
import Calendar from './pages/Calendar';
import GoogleCalendarCallback from './pages/GoogleCalendarCallback';
import Admin from './pages/Admin';
import AdminNewsletters from './pages/AdminNewsletters';
import AdminNewsletterEvents from './pages/AdminNewsletterEvents';
import AdminNewsletterPhotos from './pages/AdminNewsletterPhotos';
import AdminJobs from './pages/AdminJobs';
import Splash from './pages/Splash';
import CodyGuide from './pages/CodyGuide';
import AppsCatalogue from './pages/AppsCatalogue';
import AppDetail from './pages/AppDetail';
import Survey from './pages/Survey';
import DeactivatedNotice from './components/DeactivatedNotice';
import CareerMap from './pages/CareerMap';
// Home.jsx · former HR home page · archived. /home redirects to / now.

function App() {
  // Force light mode globally · the brand palette was tuned for light
  // surfaces; the dark variant left several pages looking off and the
  // toggle has been removed from the UI. Any leftover preference is
  // cleared so returning users come back to the supported theme.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    if (localStorage.getItem('theme')) {
      localStorage.removeItem('theme');
    }
  }, []);

  const guarded = (el) => <PrivateRoute>{el}</PrivateRoute>;

  return (
    <LocaleProvider>
    <AuthProvider>
      <ImpersonationProvider>
      <AppCatalogueProvider>
      <FavoritesProvider>
      <ReviewQueueProvider>
        <DeactivatedNotice />
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/sso-callback" element={<SSOCallback />} />
            {/* The hub landing lives at / · /home and /dashboard are legacy
                 paths kept as redirects so old bookmarks/emails still land. */}
            <Route path="/" element={guarded(<Splash />)} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/splash" element={<Navigate to="/" replace />} />
            <Route path="/expenses" element={guarded(<ExpensesList />)} />
            <Route path="/expenses/new" element={guarded(<ExpenseEditor />)} />
            <Route path="/expenses/:id" element={guarded(<ExpenseEditor />)} />
            <Route path="/review" element={guarded(<ReviewQueue />)} />
            <Route path="/review/:id" element={guarded(<ReviewDetail />)} />
            <Route path="/resources" element={guarded(<Resources />)} />
            {/* MANEVA · the newsletter category gets its own dedicated
                layout (homepage + episode detail) · these must sit
                before the generic /resources/:category catchall so
                they take precedence. */}
            <Route path="/resources/newsletters" element={guarded(<Newsletters />)} />
            <Route path="/resources/newsletters/:slug" element={guarded(<Newsletters />)} />
            <Route path="/resources/:category" element={guarded(<Resources />)} />
            <Route path="/directory" element={guarded(<Directory />)} />
            <Route path="/calendar" element={guarded(<Calendar />)} />
            <Route path="/calendar/google/callback" element={guarded(<GoogleCalendarCallback />)} />
            <Route path="/admin" element={guarded(<Admin />)} />
            <Route path="/admin/newsletters" element={guarded(<AdminNewsletters />)} />
            <Route path="/admin/newsletters/:id" element={guarded(<AdminNewsletters />)} />
            <Route path="/admin/newsletter-events" element={guarded(<AdminNewsletterEvents />)} />
            <Route path="/admin/newsletter-photos" element={guarded(<AdminNewsletterPhotos />)} />
            <Route path="/admin/jobs" element={guarded(<AdminJobs />)} />
            <Route path="/admin/jobs/:id" element={guarded(<AdminJobs />)} />
            {/* Last: the static /admin/* routes above outrank these dynamic ones,
                so they only catch the in-page tabs. The deeper segments make
                each open panel a link you can paste to someone —
                /admin/apps/sangly-encodage/access lands straight on that
                page's access list. */}
            <Route path="/admin/:tab" element={guarded(<Admin />)} />
            <Route path="/admin/:tab/:item" element={guarded(<Admin />)} />
            <Route path="/admin/:tab/:item/:action" element={guarded(<Admin />)} />
            <Route path="/cody" element={guarded(<CodyGuide />)} />
            <Route path="/apps" element={guarded(<AppsCatalogue />)} />
            <Route path="/apps/:slug" element={guarded(<AppDetail />)} />
            <Route path="/surveys/:slug" element={guarded(<Survey />)} />
            <Route path="/surveys/:slug/:view" element={guarded(<Survey />)} />
            {/* Career map · not linked anywhere until HR publishes it; the API
                 only returns unpublished maps to HR and admins. */}
            <Route path="/career-map" element={guarded(<CareerMap />)} />
          </Routes>
        </Router>
      </ReviewQueueProvider>
      </FavoritesProvider>
      </AppCatalogueProvider>
      </ImpersonationProvider>
    </AuthProvider>
    </LocaleProvider>
  );
}

export default App;
